import { PlusOutlined, PrinterOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Card, Dropdown, Form, Input, Modal, Space, Switch, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { api, unwrap, type Schemas } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { printPdf } from "./packLabel";

type Desk = Schemas["Desk"];
type Values = Schemas["DeskInput"];

/**
 * Packing desks: a PDA packing on the floor checks in at one by scanning the
 * QR stuck on it, so each parcel records where and from when it was packed.
 */
export function DeskList({ warehouseId }: { warehouseId: string }) {
  const { t } = useI18n();
  const { can } = useAuth();
  const { modal, message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const queryClient = useQueryClient();
  const manage = can("warehouse.manage");
  const [editing, setEditing] = useState<Desk | "new" | null>(null);
  const [form] = Form.useForm<Values>();
  const list = useQuery({
    queryKey: ["desks", warehouseId],
    queryFn: () => unwrap(api.GET("/v1/warehouses/{warehouseID}/desks", { params: { path: { warehouseID: warehouseId } } })),
  });
  const done = () => void queryClient.invalidateQueries({ queryKey: ["desks"] });
  const save = useMutation({
    mutationFn: (v: Values) =>
      editing === "new" || !editing
        ? unwrap(api.POST("/v1/warehouses/{warehouseID}/desks", { params: { path: { warehouseID: warehouseId } }, body: v }))
        : unwrap(api.PUT("/v1/desks/{deskID}", { params: { path: { deskID: editing.id } }, body: v })),
    onSuccess: () => {
      setEditing(null);
      done();
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/v1/desks/{deskID}", { params: { path: { deskID: id } } })),
    onSuccess: done,
    onError: (err) => void message.error(errorText(err)),
  });
  const [printing, setPrinting] = useState(false);
  const printTags = async (paper: "thermal" | "a4") => {
    setPrinting(true);
    try {
      printPdf(await unwrap(api.GET("/v1/warehouses/{warehouseID}/desks/labels", { params: { path: { warehouseID: warehouseId }, query: { paper } }, parseAs: "blob" })));
    } catch (err) {
      void message.error(errorText(err));
    } finally {
      setPrinting(false);
    }
  };
  const open = (d: Desk | "new") => {
    form.setFieldsValue(d === "new" ? { code: "", name: "", active: true } : { code: d.code, name: d.name, active: d.active });
    setEditing(d);
  };
  const items = list.data?.items ?? [];

  return (
    <Card
      size="small"
      title={t("wms.desk.title")}
      style={{ marginTop: 16 }}
      extra={
        <Space wrap>
          <Dropdown
            disabled={items.every((d) => !d.active)}
            menu={{
              items: [
                { key: "thermal", label: t("wms.pkg.printThermal") },
                { key: "a4", label: t("wms.pkg.printA4") },
              ],
              onClick: ({ key }) => void printTags(key as "thermal" | "a4"),
            }}
          >
            <Button size="small" icon={<PrinterOutlined />} loading={printing}>
              {t("wms.desk.printQr")}
            </Button>
          </Dropdown>
          {manage && (
            <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => open("new")}>
              {t("wms.desk.add")}
            </Button>
          )}
        </Space>
      }
    >
      <Typography.Paragraph type="secondary">{t("wms.desk.help")}</Typography.Paragraph>
      <Table
        size="small"
        rowKey="id"
        loading={list.isLoading}
        dataSource={items}
        pagination={false}
        locale={{ emptyText: t("wms.desk.empty") }}
        columns={[
          { title: t("wms.desk.code"), dataIndex: "code", render: (v: string) => <span className="z-num">{v}</span> },
          { title: t("wms.desk.name"), dataIndex: "name" },
          { title: t("common.status"), dataIndex: "active", render: (v: boolean) => (v ? <Tag color="success">{t("wms.desk.active")}</Tag> : <Tag>{t("wms.desk.inactive")}</Tag>) },
          ...(manage
            ? [
                {
                  key: "actions",
                  width: 160,
                  render: (_: unknown, d: Desk) => (
                    <Space>
                      <Button size="small" onClick={() => open(d)}>
                        {t("common.edit")}
                      </Button>
                      <Button
                        size="small"
                        danger
                        onClick={() =>
                          modal.confirm({ title: t("wms.desk.deleteConfirm", { code: d.code }), okButtonProps: { danger: true }, onOk: () => remove.mutateAsync(d.id) })
                        }
                      >
                        {t("common.delete")}
                      </Button>
                    </Space>
                  ),
                },
              ]
            : []),
        ]}
      />
      <Modal
        open={!!editing}
        title={editing === "new" ? t("wms.desk.add") : t("wms.desk.edit")}
        onCancel={() => setEditing(null)}
        onOk={() => form.submit()}
        confirmLoading={save.isPending}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
          <Form.Item name="code" label={t("wms.desk.code")} extra={t("wms.desk.codeHelp")} rules={[{ required: true }, { pattern: /^[A-Za-z0-9][A-Za-z0-9-]{0,31}$/ }]}>
            <Input maxLength={32} style={{ textTransform: "uppercase" }} />
          </Form.Item>
          <Form.Item name="name" label={t("wms.desk.name")}>
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item name="active" label={t("wms.desk.active")} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
